import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { policiesApi } from '../services/api';
import { Policy } from '../types';
import {
    Shield,
    ShieldCheck,
    Plus,
    Search,
    Trash2,
    Pencil,
    ScrollText,
    SearchX,
    ToggleLeft,
    ToggleRight,
    Globe,
    Code,
    X,
    AlertTriangle,
    CheckCircle,
    Network,
    Ban,
    ChevronDown,
    ChevronUp,
} from 'lucide-react';
import { clsx } from 'clsx';
import { toast } from '@/lib/toast';
import { Banner, Button, Card, CardButton, Checkbox, Chip, EmptyState, IconButton, Input, ListPageSkeleton, Modal, PageHeader, SearchField, Select, StatCard, StatusBadge, Textarea, Timestamp, MonoCell } from '@/components/ds-zta';
import StatValue from '@/components/StatValue';
import { useUrlState } from '@/lib/useUrlState';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useUser } from '@/contexts/UserContext';
import { cidrError, isValidCIDR } from '@/features/policies/cidr';
import { checkRego, hasRegoErrors } from '@/features/policies/regoCheck';
import { authorOf, fieldChanges, type PolicyFormValues } from '@/features/policies/policyForm';
import {
    AclSimulatePanel,
    FieldChanges,
    HistoryNote,
    RegoDiff,
    RegoIssueList,
    RegoSimulatePanel,
    TypedConfirmField,
    TypedConfirmModal,
} from '@/features/policies/PolicyReviewParts';
import { sampleInputError, typedMatches } from '@/features/policies/reviewChecks';

/* ─── Helper: get policy type from metadata ─── */
function getPolicyType(p: Policy): 'opa' | 'ip_acl' {
    return p.metadata?.policy_type === 'ip_acl' ? 'ip_acl' : 'opa';
}

function getCIDRs(p: Policy, key: 'allow_cidrs' | 'deny_cidrs'): string[] {
    const v = p.metadata?.[key];
    return Array.isArray(v) ? v : [];
}

function getSampleInput(p: Policy | null): string {
    const v = p?.metadata?.sample_input;
    return typeof v === 'string' ? v : '';
}

/* What publishing a saved policy would put live, checked before the typed
   confirmation (F18). Errors block publishing; warnings are shown. */
function publishChecks(p: Policy): { errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (getPolicyType(p) === 'opa') {
        for (const i of checkRego(p.rego_content || '')) {
            (i.severity === 'error' ? errors : warnings).push(`Line ${i.line}: ${i.message}`);
        }
    } else {
        const allow = getCIDRs(p, 'allow_cidrs');
        const deny = getCIDRs(p, 'deny_cidrs');
        const bad = [...allow, ...deny].filter((c) => !isValidCIDR(c));
        if (bad.length) errors.push(`Invalid IP / CIDR entries: ${bad.join(', ')}. Edit the policy to fix them.`);
        if (allow.length === 0 && deny.length === 0) errors.push('The policy has no entries. Edit it to add allowed or denied IPs.');
        else if (allow.length === 0) warnings.push('The allow list is empty, so this policy allows every IP that is not denied.');
    }
    return { errors, warnings };
}

/* Policies read per request. GET /policies pages by limit/offset and has no
   search or type filter, so search and type narrow the loaded policies, and
   the list says how many of the total are loaded (design eval F9: it used to
   stop at 100 without saying so). */
const PAGE_SIZE = 100;
type TypeFilter = 'all' | 'opa' | 'ip_acl';
const TYPE_FILTERS: readonly TypeFilter[] = ['all', 'opa', 'ip_acl'];

/* ─── Policies Page ──────────────────────────────────── */
export default function Policies() {
    const queryClient = useQueryClient();
    const { confirm } = useDialogs();
    // Search, type and the open policy live in the URL (design eval F24).
    const [url, setUrl] = useUrlState({ q: '', type: 'all', open: '' });
    const searchTerm = url.q;
    const typeFilter: TypeFilter = (TYPE_FILTERS as readonly string[]).includes(url.type) ? (url.type as TypeFilter) : 'all';
    const expandedId = url.open || null;
    const setExpandedId = (id: string | null) => setUrl({ open: id }, { replace: true });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editPolicy, setEditPolicy] = useState<Policy | null>(null);
    const [publishTarget, setPublishTarget] = useState<Policy | null>(null);

    const { data, refetch, isFetching, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
        'policies',
        ({ pageParam = 0 }) => policiesApi.list(false, PAGE_SIZE, pageParam),
        {
            retry: 1,
            refetchOnWindowFocus: false,
            getNextPageParam: (last, pages) => {
                const loaded = pages.reduce((n, p) => n + (p.data?.policies?.length ?? 0), 0);
                return loaded < (last.data?.total ?? 0) && (last.data?.policies?.length ?? 0) > 0 ? loaded : undefined;
            },
        }
    );

    const policies: Policy[] = (data?.pages ?? []).flatMap((p) => p.data?.policies ?? []);
    const totalPolicies = data?.pages?.[data.pages.length - 1]?.data?.total ?? policies.length;
    const partial = policies.length < totalPolicies;
    const loadedCaption = partial ? `of ${policies.length} loaded` : undefined;

    const deleteMutation = useMutation(
        async (id: string) => { await policiesApi.delete(id); },
        {
            onSuccess: () => {
                toast.success('Policy deleted');
                queryClient.invalidateQueries('policies');
            },
            onError: () => { toast.error('Failed to delete policy'); },
        }
    );

    const toggleMutation = useMutation(
        async ({ id, active }: { id: string; active: boolean }) => {
            await policiesApi.update(id, { active });
        },
        {
            onSuccess: (_data, { active }) => {
                toast.success(active ? 'Policy published' : 'Policy deactivated; it is a draft again');
                queryClient.invalidateQueries('policies');
            },
            onError: (_err, { active }) => { toast.error(active ? 'Failed to publish policy' : 'Failed to deactivate policy'); },
        }
    );

    const filteredPolicies = policies.filter((p) => {
        const matchesSearch =
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.created_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.metadata?.description || '').toLowerCase().includes(searchTerm.toLowerCase());
        const pType = getPolicyType(p);
        const matchesType = typeFilter === 'all' || pType === typeFilter;
        return matchesSearch && matchesType;
    });

    const opaPolicies = policies.filter((p) => getPolicyType(p) === 'opa');
    const ipAclPolicies = policies.filter((p) => getPolicyType(p) === 'ip_acl');
    const activePolicies = policies.filter((p) => p.active);
    const inactivePolicies = policies.filter((p) => !p.active);

    const handleDelete = async (id: string, name: string) => {
        if (await confirm({
            title: 'Delete policy?',
            message: `Delete policy "${name}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            variant: 'danger',
        })) {
            deleteMutation.mutate(id);
        }
    };

    // F5: activating or deactivating a policy changes access decisions for every
    // request it matches, so it confirms and names the policy first. F18:
    // publishing (activating) is a typed confirmation behind the policy checks.
    const handleToggle = async (policy: Policy) => {
        if (!policy.active) {
            setPublishTarget(policy);
            return;
        }
        if (await confirm({
            title: 'Deactivate policy?',
            message: `Deactivate policy "${policy.name}"? It will stop being enforced on matching requests immediately and go back to draft.`,
            confirmLabel: 'Deactivate',
            variant: 'warning',
        })) {
            toggleMutation.mutate({ id: policy.id, active: false });
        }
    };
    const publishCheck = publishTarget ? publishChecks(publishTarget) : null;

    return (
        <div className="animate-fade-in space-y-6">
            {/* Header */}
            <PageHeader
                icon={<ScrollText />}
                title="Policies"
                description="Manage OPA authorization policies and IP-based access control rules"
                primaryAction={
                    <Button onClick={() => { setEditPolicy(null); setShowCreateModal(true); }} leadingIcon={<Plus className="h-4 w-4" />}>
                        Create Policy
                    </Button>
                }
                onRefresh={() => refetch()}
                refreshing={isFetching}
            />

            {/* Stat Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard icon={<Shield />} label="Total Policies" value={totalPolicies} />
                <StatCard icon={<Code />} label="OPA Rego" value={<StatValue value={opaPolicies.length} caption={loadedCaption} />} />
                <StatCard icon={<Globe />} label="IP ACL" value={<StatValue value={ipAclPolicies.length} caption={loadedCaption} />} />
                <StatCard icon={<ShieldCheck />} label="Active" accent="success" value={<StatValue value={activePolicies.length} caption={loadedCaption} />} />
            </div>

            {/* Search + Filter */}
            <Card className="p-4 overflow-visible">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                        <SearchField
                          label="Search by name, creator, or description"
                          placeholder="Search by name, creator, or description..."
                          value={searchTerm}
                          onChange={(value) => setUrl({ q: value, open: null }, { replace: true })}
                        />
                    </div>
                    {/* A Select, not a row of 27px chips: every control in this
                        row is the 40px md size (design eval F46). */}
                    <div className="sm:w-44">
                        <Select
                            className="gap-0"
                            label={<span className="sr-only">Policy type</span>}
                            value={typeFilter}
                            onChange={(e) => setUrl({ type: e.target.value, open: null })}
                        >
                            <option value="all">All Types</option>
                            <option value="opa">OPA Rego</option>
                            <option value="ip_acl">IP ACL</option>
                        </Select>
                    </div>
                </div>
            </Card>

            {/* Policies List */}
            <Card className="overflow-hidden">
                {isLoading ? (
                    <ListPageSkeleton className="p-5" filterCount={0} rowCount={6} />
                ) : isError ? (
                    <EmptyState
                        icon={<AlertTriangle />}
                        title="Failed to load policies"
                        description="Something went wrong while loading policies."
                        action={<Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>}
                    />
                ) : filteredPolicies.length === 0 ? (
                    policies.length === 0 ? (
                        <EmptyState
                            icon={<Shield />}
                            title="No policies yet"
                            description="Create your first policy to get started."
                            action={
                                <Button size="sm" onClick={() => { setEditPolicy(null); setShowCreateModal(true); }} leadingIcon={<Plus className="h-4 w-4" />}>
                                    Create Policy
                                </Button>
                            }
                        />
                    ) : (
                        <EmptyState
                            icon={<SearchX />}
                            title={searchTerm ? `No policies match "${searchTerm}"` : 'No policies match this filter'}
                            description="Try adjusting your search or filter."
                        />
                    )
                ) : (
                    <div className="divide-y divide-[var(--d2d-border)]">
                        {filteredPolicies.map((policy) => {
                            const pType = getPolicyType(policy);
                            const allowCIDRs = getCIDRs(policy, 'allow_cidrs');
                            const denyCIDRs = getCIDRs(policy, 'deny_cidrs');
                            const isExpanded = expandedId === policy.id;

                            return (
                                <div key={policy.id}>
                                    {/* The row's summary is a real button (design eval F43), so
                                        it expands from the keyboard; the actions sit beside it. */}
                                    <div className="flex items-center justify-between pr-5 hover:bg-[var(--ds-color-bg-hover)] transition-colors group">
                                        <CardButton
                                            className="flex flex-1 items-center gap-4 min-w-0 pl-5 py-4"
                                            aria-expanded={isExpanded}
                                            aria-controls={`policy-detail-${policy.id}`}
                                            onClick={() => setExpandedId(isExpanded ? null : policy.id)}
                                        >
                                            <div
                                                className={clsx(
                                                    'flex items-center justify-center h-10 w-10 rounded-xl flex-shrink-0',
                                                    pType === 'opa'
                                                        ? 'bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)]'
                                                        : 'bg-[var(--zta-tint-warning)] text-[var(--d2d-warning)]'
                                                )}
                                            >
                                                {pType === 'opa' ? <Code className="h-5 w-5" /> : <Globe className="h-5 w-5" />}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-medium text-[var(--d2d-text-primary)] truncate">
                                                        {policy.name}
                                                    </span>
                                                    <Chip variant={pType === 'opa' ? 'neutral' : 'warning'} className="uppercase">
                                                        {pType === 'opa' ? 'OPA Rego' : 'IP ACL'}
                                                    </Chip>
                                                    {policy.active ? (
                                                        <StatusBadge value="active" shape="chip">Active</StatusBadge>
                                                    ) : (
                                                        <StatusBadge value="draft" shape="chip">Draft</StatusBadge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-xs text-[var(--d2d-text-muted)]">
                                                    {policy.metadata?.description && (
                                                        <span className="truncate max-w-[300px]">{policy.metadata.description}</span>
                                                    )}
                                                    <span className="font-mono tabular-nums">v{policy.version}</span>
                                                    <span>by {policy.created_by}</span>
                                                    <Timestamp value={policy.created_at} noSeconds />
                                                    {policy.metadata?.updated_by && (
                                                        <span>edited by {policy.metadata.updated_by}</span>
                                                    )}
                                                    {pType === 'ip_acl' && (
                                                        <>
                                                            {allowCIDRs.length > 0 && (
                                                                <span className="flex items-center gap-1 text-[var(--d2d-success)]">
                                                                    <Network className="h-3 w-3" />
                                                                    {allowCIDRs.length} allow
                                                                </span>
                                                            )}
                                                            {denyCIDRs.length > 0 && (
                                                                <span className="flex items-center gap-1 text-[var(--danger-text)]">
                                                                    <Ban className="h-3 w-3" />
                                                                    {denyCIDRs.length} deny
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {isExpanded ? <ChevronUp aria-hidden className="ml-auto h-4 w-4 flex-shrink-0 text-[var(--d2d-text-muted)]" /> : <ChevronDown aria-hidden className="ml-auto h-4 w-4 flex-shrink-0 text-[var(--d2d-text-muted)]" />}
                                        </CardButton>
                                        <div className="flex items-center justify-end gap-1 flex-shrink-0 pl-2">
                                            {policy.active ? (
                                                <IconButton
                                                    size="sm"
                                                    aria-label="Deactivate"
                                                    tooltip="Deactivate policy (back to draft)"
                                                    onClick={() => handleToggle(policy)}
                                                    icon={<ToggleRight className="h-5 w-5 text-[var(--d2d-success)]" />}
                                                />
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    aria-label={`Publish ${policy.name}`}
                                                    leadingIcon={<ToggleLeft className="h-4 w-4" />}
                                                    onClick={() => handleToggle(policy)}
                                                >
                                                    Publish
                                                </Button>
                                            )}
                                            <IconButton
                                                size="sm"
                                                aria-label="Edit"
                                                tooltip="Edit policy"
                                                onClick={() => { setEditPolicy(policy); setShowCreateModal(true); }}
                                                icon={<Pencil className="h-4 w-4" />}
                                            />
                                            <IconButton
                                                size="sm"
                                                aria-label="Delete"
                                                tooltip="Delete policy"
                                                className="hover:text-[var(--danger-text)]"
                                                onClick={() => handleDelete(policy.id, policy.name)}
                                                icon={<Trash2 className="h-4 w-4" />}
                                            />
                                        </div>
                                    </div>

                                    {/* Expanded detail view */}
                                    {isExpanded && (
                                        <div id={`policy-detail-${policy.id}`} className="px-5 pb-4 bg-[var(--d2d-bg-secondary)] border-t border-[var(--d2d-border)]">
                                            {pType === 'opa' ? (
                                                <div className="mt-3">
                                                    <label className="block text-label font-semibold text-[var(--d2d-text-muted)] mb-1.5 uppercase">
                                                        Rego Policy
                                                    </label>
                                                    <pre className="p-4 bg-[var(--d2d-bg-secondary)] text-[var(--d2d-text-primary)] border border-[var(--d2d-border)] rounded-xl text-xs font-mono overflow-x-auto max-h-64 whitespace-pre-wrap">
                                                        {policy.rego_content || '(empty)'}
                                                    </pre>
                                                </div>
                                            ) : (
                                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="flex items-center gap-1.5 text-label font-semibold text-[var(--d2d-success)] mb-2 uppercase">
                                                            <Network className="h-3.5 w-3.5" /> Allowed CIDRs
                                                        </label>
                                                        {allowCIDRs.length === 0 ? (
                                                            <p className="flex items-center gap-1.5 text-xs text-[var(--d2d-warning)]">
                                                                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                                                                Allows every IP: no allow rules, so every IP not denied is allowed.
                                                            </p>
                                                        ) : (
                                                            <div className="space-y-1.5">
                                                                {allowCIDRs.map((cidr, i) => (
                                                                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-[var(--zta-tint-success)] rounded-lg border border-[var(--zta-tint-success-border)]">
                                                                        <CheckCircle className="h-3.5 w-3.5 text-[var(--d2d-success)] flex-shrink-0" />
                                                                        <MonoCell value={cidr} className="text-sm text-[var(--d2d-success)]" />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label className="flex items-center gap-1.5 text-label font-semibold text-[var(--danger-text)] mb-2 uppercase">
                                                            <Ban className="h-3.5 w-3.5" /> Denied CIDRs
                                                        </label>
                                                        {denyCIDRs.length === 0 ? (
                                                            <p className="text-xs text-[var(--d2d-text-muted)] italic">No deny rules</p>
                                                        ) : (
                                                            <div className="space-y-1.5">
                                                                {denyCIDRs.map((cidr, i) => (
                                                                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-[var(--zta-tint-danger)] rounded-lg border border-[var(--zta-tint-danger-border)]">
                                                                        <Ban className="h-3.5 w-3.5 text-[var(--danger-text)] flex-shrink-0" />
                                                                        <MonoCell value={cidr} className="text-sm text-[var(--danger-text)]" />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="mt-4 space-y-1.5">
                                                <label className="block text-label font-semibold text-[var(--d2d-text-muted)] uppercase">
                                                    Version
                                                </label>
                                                <p className="text-xs text-[var(--d2d-text-secondary)]">
                                                    <span className="font-mono tabular-nums">v{policy.version}</span>
                                                    {' · '}{policy.active ? 'Published (active)' : 'Draft (not enforced)'}
                                                    {' · created by '}{policy.created_by}{' '}
                                                    <Timestamp value={policy.created_at} noSeconds />
                                                    {policy.metadata?.updated_by && (
                                                        <>
                                                            {' · last edited by '}{policy.metadata.updated_by}
                                                            {policy.metadata?.updated_at && <>{' '}<Timestamp value={policy.metadata.updated_at} noSeconds /></>}
                                                        </>
                                                    )}
                                                </p>
                                                <HistoryNote />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {!isLoading && !isError && policies.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[var(--d2d-border)] text-sm text-[var(--d2d-text-secondary)]">
                        <span aria-live="polite">
                            {partial
                                ? `Showing ${policies.length} of ${totalPolicies} policies. Search and the type filter apply to the loaded ${policies.length}.`
                                : `All ${totalPolicies} policies loaded.`}
                        </span>
                        {hasNextPage && (
                            <Button size="sm" variant="secondary" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                                Load {Math.min(PAGE_SIZE, totalPolicies - policies.length)} more
                            </Button>
                        )}
                    </div>
                )}
            </Card>

            {/* Create / Edit Modal */}
            <PolicyFormModal
                isOpen={showCreateModal}
                onClose={() => { setShowCreateModal(false); setEditPolicy(null); }}
                onSaved={() => {
                    queryClient.invalidateQueries('policies');
                    setShowCreateModal(false);
                    setEditPolicy(null);
                }}
                existingPolicy={editPolicy}
            />

            {/* Publish = activate, behind the checks and a typed confirmation (F18). */}
            <TypedConfirmModal
                open={!!publishTarget}
                title={publishTarget ? `Publish ${publishTarget.name}?` : 'Publish policy?'}
                expected={publishTarget?.name ?? ''}
                action="publish it"
                confirmLabel="Publish"
                blocked={!!publishCheck?.errors.length}
                onResult={(ok) => {
                    if (ok && publishTarget) toggleMutation.mutate({ id: publishTarget.id, active: true });
                    setPublishTarget(null);
                }}
            >
                {publishTarget && publishCheck && (
                    <>
                        <p className="text-sm text-[var(--d2d-text-secondary)]">
                            v{publishTarget.version} of this {getPolicyType(publishTarget) === 'opa' ? 'Rego policy' : 'IP ACL'} will be
                            enforced on matching requests immediately.
                        </p>
                        {publishCheck.errors.length > 0 && (
                            <Banner variant="danger" title="Can't publish yet">
                                <ul className="list-disc pl-4 space-y-0.5">
                                    {publishCheck.errors.map((e) => <li key={e}>{e}</li>)}
                                </ul>
                            </Banner>
                        )}
                        {publishCheck.warnings.length > 0 && (
                            <Banner variant="warning" title="Check before publishing">
                                <ul className="list-disc pl-4 space-y-0.5">
                                    {publishCheck.warnings.map((w) => <li key={w}>{w}</li>)}
                                </ul>
                            </Banner>
                        )}
                        {getPolicyType(publishTarget) === 'opa' && publishCheck.errors.length === 0 && (
                            <p className="text-xs text-[var(--d2d-text-muted)]">
                                Rego checks run in your browser and are not a compile; the server may still reject the policy.
                            </p>
                        )}
                    </>
                )}
            </TypedConfirmModal>
        </div>
    );
}

/* ─── Policy Form Modal ─────────────────────────────── */
/* F18: draft -> validate -> simulate -> diff -> publish.
   - New policies save as inactive drafts; publishing is a separate, typed step.
   - Rego gets structural checks with line numbers (client-side; the API has
     no validate/compile endpoint). CIDRs are parsed properly, a typed but
     unadded entry blocks the save, and an empty allow list must be
     acknowledged because it allows every IP that is not denied.
   - An edit is reviewed as a diff against the saved version and sends
     version + 1; created_by stays the original author and updated_by is the
     signed-in user (in metadata: the Policy type has no updated_by). */
function PolicyFormModal({
    isOpen,
    onClose,
    onSaved,
    existingPolicy,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => void;
    existingPolicy: Policy | null;
}) {
    const isEdit = !!existingPolicy;
    const { userInfo } = useUser();
    const author = authorOf(userInfo);
    const [policyType, setPolicyType] = useState<'opa' | 'ip_acl'>(
        existingPolicy ? getPolicyType(existingPolicy) : 'ip_acl'
    );
    const [name, setName] = useState(existingPolicy?.name || '');
    const [description, setDescription] = useState(existingPolicy?.metadata?.description || '');
    const [regoContent, setRegoContent] = useState(existingPolicy?.rego_content || '');
    const [sampleInput, setSampleInput] = useState(getSampleInput(existingPolicy));
    const [allowCIDRs, setAllowCIDRs] = useState<string[]>(
        existingPolicy ? getCIDRs(existingPolicy, 'allow_cidrs') : []
    );
    const [denyCIDRs, setDenyCIDRs] = useState<string[]>(
        existingPolicy ? getCIDRs(existingPolicy, 'deny_cidrs') : []
    );
    const [newAllowCIDR, setNewAllowCIDR] = useState('');
    const [newDenyCIDR, setNewDenyCIDR] = useState('');
    const [allowAllAck, setAllowAllAck] = useState(false);
    const [step, setStep] = useState<'edit' | 'review'>('edit');
    const [typedName, setTypedName] = useState('');
    // Inline errors appear once a save has been attempted (or a CIDR added).
    const [attempted, setAttempted] = useState(false);
    const [cidrInputError, setCidrInputError] = useState<{ allow?: string; deny?: string }>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const resetForm = () => {
        setPolicyType(existingPolicy ? getPolicyType(existingPolicy) : 'ip_acl');
        setName(existingPolicy?.name ?? '');
        setDescription(existingPolicy?.metadata?.description || '');
        setRegoContent(existingPolicy?.rego_content || '');
        setSampleInput(getSampleInput(existingPolicy));
        setAllowCIDRs(existingPolicy ? getCIDRs(existingPolicy, 'allow_cidrs') : []);
        setDenyCIDRs(existingPolicy ? getCIDRs(existingPolicy, 'deny_cidrs') : []);
        setNewAllowCIDR('');
        setNewDenyCIDR('');
        setAllowAllAck(false);
        setStep('edit');
        setTypedName('');
        setAttempted(false);
        setCidrInputError({});
        setError('');
    };

    // The Headless UI Transition this replaced ran resetForm in afterEnter.
    useEffect(() => {
        if (isOpen) resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const isAcl = policyType === 'ip_acl';
    const regoIssues = useMemo(() => (isAcl ? [] : checkRego(regoContent)), [isAcl, regoContent]);
    const sampleErr = isAcl ? null : sampleInputError(sampleInput);
    const allowListEmpty = isAcl && allowCIDRs.length === 0;

    const addCIDR = (type: 'allow' | 'deny') => {
        const value = type === 'allow' ? newAllowCIDR.trim() : newDenyCIDR.trim();
        if (!value) return;
        const err = cidrError(value);
        if (err) {
            setCidrInputError((e) => ({ ...e, [type]: err }));
            return;
        }
        setCidrInputError((e) => ({ ...e, [type]: undefined }));
        if (type === 'allow') {
            if (!allowCIDRs.includes(value)) setAllowCIDRs([...allowCIDRs, value]);
            setNewAllowCIDR('');
        } else {
            if (!denyCIDRs.includes(value)) setDenyCIDRs([...denyCIDRs, value]);
            setNewDenyCIDR('');
        }
    };

    const removeCIDR = (type: 'allow' | 'deny', index: number) => {
        if (type === 'allow') {
            setAllowCIDRs(allowCIDRs.filter((_, i) => i !== index));
        } else {
            setDenyCIDRs(denyCIDRs.filter((_, i) => i !== index));
        }
    };

    // A typed-but-not-added entry used to be dropped silently on save.
    const pendingError = (value: string) =>
        value.trim() ? `"${value.trim()}" hasn't been added. Press Add (or Enter), or clear the field.` : undefined;

    const blockers: string[] = [];
    if (!name.trim()) blockers.push('Policy name is required.');
    if (!author) blockers.push("Can't tell who you are, so the author can't be recorded. Sign in again and retry.");
    if (isAcl) {
        if (newAllowCIDR.trim()) blockers.push('An allowed IP / CIDR was typed but not added.');
        if (newDenyCIDR.trim()) blockers.push('A denied IP / CIDR was typed but not added.');
        if (allowCIDRs.length === 0 && denyCIDRs.length === 0) blockers.push('Add at least one allowed or denied IP / CIDR.');
        else if (allowListEmpty && !allowAllAck) blockers.push('Acknowledge that an empty allow list allows every IP that is not denied.');
        const bad = [...allowCIDRs, ...denyCIDRs].filter((c) => !isValidCIDR(c));
        if (bad.length) blockers.push(`Invalid saved entries: ${bad.join(', ')}. Remove them.`);
    } else {
        if (hasRegoErrors(regoIssues)) blockers.push('Fix the Rego errors listed under the editor.');
        if (sampleErr) blockers.push('The sample input is not valid JSON.');
    }

    const savedValues: PolicyFormValues | null = existingPolicy
        ? {
            name: existingPolicy.name,
            description: existingPolicy.metadata?.description || '',
            regoContent: existingPolicy.rego_content || '',
            allowCIDRs: getCIDRs(existingPolicy, 'allow_cidrs'),
            denyCIDRs: getCIDRs(existingPolicy, 'deny_cidrs'),
            sampleInput: getSampleInput(existingPolicy),
        }
        : null;
    const currentValues: PolicyFormValues = { name, description, regoContent, allowCIDRs, denyCIDRs, sampleInput };
    const changes = savedValues ? fieldChanges(savedValues, currentValues, isAcl) : [];
    const regoChanged = !!savedValues && !isAcl && savedValues.regoContent !== regoContent;
    const hasChanges = changes.length > 0 || regoChanged;
    const nextVersion = (existingPolicy?.version ?? 0) + 1;
    // Saving an edit to an active policy is publishing: it applies at once.
    const needsTypedConfirm = !!existingPolicy?.active;

    const goNext = () => {
        setAttempted(true);
        if (blockers.length) {
            setError(blockers[0]);
            return;
        }
        setError('');
        if (isEdit) setStep('review');
        else handleSave();
    };

    const handleSave = async () => {
        if (blockers.length) { setError(blockers[0]); return; }
        setSaving(true);
        setError('');

        try {
            const metadata: Record<string, any> = {
                ...(existingPolicy?.metadata ?? {}),
                policy_type: policyType,
                description: description.trim(),
            };
            if (isAcl) {
                metadata.allow_cidrs = allowCIDRs;
                metadata.deny_cidrs = denyCIDRs;
                delete metadata.sample_input;
            } else {
                delete metadata.allow_cidrs;
                delete metadata.deny_cidrs;
                if (sampleInput.trim()) metadata.sample_input = sampleInput.trim();
                else delete metadata.sample_input;
            }

            if (isEdit) {
                metadata.updated_by = author;
                metadata.updated_at = new Date().toISOString();
                await policiesApi.update(existingPolicy.id, {
                    name: existingPolicy.name,
                    // The API types carry version on the client and don't say the
                    // server bumps it, so the edit sends the next version.
                    version: nextVersion,
                    rego_content: isAcl ? '' : regoContent,
                    metadata,
                    active: existingPolicy.active,
                    created_by: existingPolicy.created_by,
                });
                toast.success(`Policy saved as v${nextVersion}`);
            } else {
                await policiesApi.create({
                    name: name.trim(),
                    version: 1,
                    rego_content: isAcl ? '' : regoContent,
                    metadata,
                    // Drafts by default: nothing is enforced until it is published.
                    active: false,
                    created_by: author as string,
                });
                toast.success('Draft saved. Publish it from the list when it is ready.');
            }

            onSaved();
        } catch (err: any) {
            const msg = err.response?.data?.error || err.message || 'Failed to save policy';
            setError(msg);
            setStep('edit');
        } finally {
            setSaving(false);
        }
    };

    const reviewSaveDisabled = saving || !hasChanges || (needsTypedConfirm && !typedMatches(existingPolicy?.name ?? '', typedName));

    return (
        <Modal
            // F18: a stray backdrop click must not throw away typed Rego / CIDRs.
            // Cancel, the close button and Escape still close it.
            dismissOnOverlay={false}
            open={isOpen}
            onClose={onClose}
            size="lg"
            title={isEdit ? (step === 'review' ? `Review changes to ${existingPolicy.name}` : 'Edit Policy') : 'Create Draft Policy'}
            description={
                isEdit
                    ? `Saved as v${existingPolicy.version}${existingPolicy.active ? ', active' : ', draft'}. Saving creates v${nextVersion}.`
                    : 'New policies are saved as drafts and are not enforced until you publish them.'
            }
            footer={
                step === 'review' ? (
                    <>
                        <Button variant="secondary" onClick={() => { setStep('edit'); setTypedName(''); }}>
                            Back to edit
                        </Button>
                        <Button onClick={handleSave} disabled={reviewSaveDisabled} loading={saving}>
                            {saving ? 'Saving…' : needsTypedConfirm ? `Save and apply v${nextVersion}` : `Save v${nextVersion}`}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={goNext} disabled={saving} loading={saving}>
                            {saving ? 'Saving…' : isEdit ? 'Review changes' : 'Save draft'}
                        </Button>
                    </>
                )
            }
        >
            {step === 'review' && existingPolicy ? (
                <div className="space-y-5">
                    {!hasChanges ? (
                        <Banner variant="info" title="Nothing changed">
                            This matches the saved v{existingPolicy.version}. Go back to make an edit.
                        </Banner>
                    ) : (
                        <>
                            {changes.length > 0 && (
                                <section aria-label="Field changes" className="space-y-2">
                                    <h3 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase">Fields</h3>
                                    <FieldChanges changes={changes} />
                                </section>
                            )}
                            {!isAcl && (
                                <section aria-label="Rego changes" className="space-y-2">
                                    <h3 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase">
                                        Rego: v{existingPolicy.version} → v{nextVersion}
                                    </h3>
                                    <RegoDiff before={existingPolicy.rego_content || ''} after={regoContent} />
                                </section>
                            )}
                            {regoIssues.length > 0 && <RegoIssueList issues={regoIssues} />}
                            <p className="text-xs text-[var(--d2d-text-muted)]">
                                Author stays {existingPolicy.created_by}; this edit is recorded as by {author}.
                            </p>
                            {needsTypedConfirm && (
                                <>
                                    <Banner variant="warning" title="This policy is active">
                                        Saving applies v{nextVersion} to matching requests immediately. To stage changes
                                        without enforcing them, deactivate the policy first.
                                    </Banner>
                                    <TypedConfirmField
                                        expected={existingPolicy.name}
                                        value={typedName}
                                        onChange={setTypedName}
                                        action="apply these changes"
                                    />
                                </>
                            )}
                        </>
                    )}
                    {error && (
                        <Banner variant="danger" title="Couldn't save">{error}</Banner>
                    )}
                </div>
            ) : (
            <div className="space-y-5">
                {/* Policy Type Selector */}
                {!isEdit && (
                    <div>
                        <label className="block text-label font-semibold text-[var(--d2d-text-muted)] mb-2 uppercase">
                            Policy Type
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <CardButton
                                aria-pressed={policyType === 'ip_acl'}
                                onClick={() => setPolicyType('ip_acl')}
                                className={clsx(
                                    'flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all',
                                    policyType === 'ip_acl'
                                        ? 'border-[var(--d2d-warning)] bg-[var(--zta-tint-warning)]'
                                        : 'border-[var(--d2d-border)] hover:border-[var(--ds-color-border-strong)]'
                                )}
                            >
                                <Globe className={clsx('h-5 w-5', policyType === 'ip_acl' ? 'text-[var(--d2d-warning)]' : 'text-[var(--d2d-text-muted)]')} />
                                <div className="text-left">
                                    <p className="text-sm font-medium text-[var(--d2d-text-primary)]">IP ACL</p>
                                    <p className="text-micro text-[var(--d2d-text-muted)]">Allow / deny by IP range</p>
                                </div>
                            </CardButton>
                            <CardButton
                                aria-pressed={policyType === 'opa'}
                                onClick={() => setPolicyType('opa')}
                                className={clsx(
                                    'flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all',
                                    policyType === 'opa'
                                        ? 'border-[var(--d2d-text-primary)] bg-[var(--ds-color-bg-subtle)]'
                                        : 'border-[var(--d2d-border)] hover:border-[var(--ds-color-border-strong)]'
                                )}
                            >
                                <Code className={clsx('h-5 w-5', policyType === 'opa' ? 'text-[var(--d2d-text-primary)]' : 'text-[var(--d2d-text-muted)]')} />
                                <div className="text-left">
                                    <p className="text-sm font-medium text-[var(--d2d-text-primary)]">OPA Rego</p>
                                    <p className="text-micro text-[var(--d2d-text-muted)]">Custom authorization logic</p>
                                </div>
                            </CardButton>
                        </div>
                    </div>
                )}

                {/* Name */}
                <Input
                    label="Policy Name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Office Network Only"
                    disabled={isEdit}
                    error={attempted && !name.trim() ? 'Policy name is required.' : undefined}
                />

                {/* Description */}
                <Input
                    label="Description"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. Restrict access to office and VPN IPs only"
                />

                {/* Type-specific fields */}
                {policyType === 'opa' ? (
                    <div className="space-y-5">
                        <div className="space-y-2">
                            <Textarea
                                label="Rego Content"
                                value={regoContent}
                                onChange={(e) => setRegoContent(e.target.value)}
                                placeholder={`package authz\n\ndefault allow := false\n\nallow {\n    input.user.role == "admin"\n}`}
                                rows={12}
                                className="[&_textarea]:font-mono"
                                style={{ tabSize: 4 }}
                                spellCheck={false}
                                aria-describedby="rego-checks"
                                error={attempted && hasRegoErrors(regoIssues) ? 'Rego has errors; see the checks below.' : undefined}
                            />
                            {(regoContent.trim() || attempted) && <RegoIssueList id="rego-checks" issues={regoIssues} />}
                        </div>
                        <section aria-label="Simulate" className="space-y-2">
                            <h3 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase">Simulate</h3>
                            <RegoSimulatePanel value={sampleInput} onChange={setSampleInput} />
                        </section>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {/* Allow CIDRs */}
                        <div>
                            <label className="flex items-center gap-1.5 text-label font-semibold text-[var(--d2d-success)] mb-2 uppercase">
                                <Network className="h-3.5 w-3.5" /> Allowed IPs / CIDRs
                            </label>
                            <p className="text-micro text-[var(--d2d-text-muted)] mb-2">
                                Only these IPs will be allowed. IPv4 or IPv6, e.g. 10.0.0.0/8 or 2001:db8::/32. An empty list allows every IP that is not denied.
                            </p>
                            <div className="flex gap-2 mb-2">
                                <Input
                                    label="Allowed IP or CIDR"
                                    hideLabel
                                    className="flex-1 [&_input]:font-mono"
                                    type="text"
                                    value={newAllowCIDR}
                                    onChange={(e) => { setNewAllowCIDR(e.target.value); setCidrInputError((x) => ({ ...x, allow: undefined })); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addCIDR('allow');
                                        }
                                    }}
                                    placeholder="e.g. 10.0.0.0/8 or 203.0.113.5"
                                    error={cidrInputError.allow ?? (attempted ? pendingError(newAllowCIDR) : undefined)}
                                />
                                <IconButton
                                    tooltip="Add allowed IP / CIDR"
                                    aria-label="Add allowed IP / CIDR"
                                    className="bg-[var(--action-primary)] text-[var(--action-primary-text)] hover:bg-[var(--action-primary-hover)]"
                                    onClick={() => addCIDR('allow')}
                                    icon={<Plus className="h-4 w-4" />}
                                />
                            </div>
                            {allowCIDRs.length > 0 && (
                                <div className="space-y-1.5">
                                    {allowCIDRs.map((cidr, i) => (
                                        <div key={i} className="flex items-center justify-between px-3 py-2 bg-[var(--zta-tint-success)] rounded-lg border border-[var(--zta-tint-success-border)]">
                                            <div className="flex items-center gap-2">
                                                <CheckCircle className="h-3.5 w-3.5 text-[var(--d2d-success)] flex-shrink-0" />
                                                <span className="text-sm font-mono text-[var(--d2d-success)]">{cidr}</span>
                                                {!isValidCIDR(cidr) && <span className="text-xs text-[var(--danger-text)]">invalid</span>}
                                            </div>
                                            <IconButton
                                                size="sm"
                                                aria-label={`Remove ${cidr}`}
                                                tooltip={`Remove ${cidr}`}
                                                className="text-[var(--d2d-success)] hover:text-[var(--danger-text)]"
                                                onClick={() => removeCIDR('allow', i)}
                                                icon={<X className="h-3.5 w-3.5" />}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {allowListEmpty && denyCIDRs.length > 0 && (
                                <div className="mt-2 space-y-2">
                                    <Banner variant="warning" title="Allows every IP">
                                        With no allowed entries this policy allows every IP address that is not on the deny list.
                                    </Banner>
                                    <Checkbox
                                        label="I understand this policy allows every IP that is not denied"
                                        checked={allowAllAck}
                                        onChange={(checked) => setAllowAllAck(checked)}
                                        error={attempted && !allowAllAck ? 'Acknowledge this, or add an allowed entry.' : undefined}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Deny CIDRs */}
                        <div>
                            <label className="flex items-center gap-1.5 text-label font-semibold text-[var(--danger-text)] mb-2 uppercase">
                                <Ban className="h-3.5 w-3.5" /> Denied IPs / CIDRs
                            </label>
                            <p className="text-micro text-[var(--d2d-text-muted)] mb-2">
                                These IPs will always be blocked (evaluated before allow rules).
                            </p>
                            <div className="flex gap-2 mb-2">
                                <Input
                                    label="Denied IP or CIDR"
                                    hideLabel
                                    className="flex-1 [&_input]:font-mono"
                                    type="text"
                                    value={newDenyCIDR}
                                    onChange={(e) => { setNewDenyCIDR(e.target.value); setCidrInputError((x) => ({ ...x, deny: undefined })); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addCIDR('deny');
                                        }
                                    }}
                                    placeholder="e.g. 192.168.1.100 or 172.16.0.0/12"
                                    error={cidrInputError.deny ?? (attempted ? pendingError(newDenyCIDR) : undefined)}
                                />
                                <IconButton
                                    tooltip="Add denied IP / CIDR"
                                    aria-label="Add denied IP / CIDR"
                                    className="border border-[var(--zta-tint-danger-border)] text-[var(--danger-text)] hover:bg-[var(--zta-tint-danger)]"
                                    onClick={() => addCIDR('deny')}
                                    icon={<Plus className="h-4 w-4" />}
                                />
                            </div>
                            {denyCIDRs.length > 0 && (
                                <div className="space-y-1.5">
                                    {denyCIDRs.map((cidr, i) => (
                                        <div key={i} className="flex items-center justify-between px-3 py-2 bg-[var(--zta-tint-danger)] rounded-lg border border-[var(--zta-tint-danger-border)]">
                                            <div className="flex items-center gap-2">
                                                <Ban className="h-3.5 w-3.5 text-[var(--danger-text)] flex-shrink-0" />
                                                <span className="text-sm font-mono text-[var(--danger-text)]">{cidr}</span>
                                                {!isValidCIDR(cidr) && <span className="text-xs text-[var(--danger-text)]">invalid</span>}
                                            </div>
                                            <IconButton
                                                size="sm"
                                                aria-label={`Remove ${cidr}`}
                                                tooltip={`Remove ${cidr}`}
                                                className="text-[var(--danger-text)]"
                                                onClick={() => removeCIDR('deny', i)}
                                                icon={<X className="h-3.5 w-3.5" />}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <section aria-label="Simulate" className="space-y-2">
                            <h3 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase">Simulate</h3>
                            <AclSimulatePanel allow={allowCIDRs} deny={denyCIDRs} />
                        </section>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div role="alert" className="flex items-center gap-2 p-3 bg-[var(--zta-tint-danger)] rounded-xl border border-[var(--zta-tint-danger-border)]">
                        <AlertTriangle className="h-4 w-4 text-[var(--danger-text)] flex-shrink-0" />
                        <p className="text-sm text-[var(--danger-text)]">{error}</p>
                    </div>
                )}
            </div>
            )}
        </Modal>
    );
}
